// StrategyAnalysis.js (fixed class method placement + Cash/Currency + Closed Ended Fund=>Equity)
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { connect } from 'react-redux';
import Masonry from 'react-masonry-component';
import AnalysisCard from 'wt-react/dist/components/AnalysisCard/AnalysisCard';
import WTText from 'wt-react/dist/components/WTText/WTText';

import {
  formatDate,
  formatPercent,
  getKeyMetrics,
  getKeyMetricsSymbols,
  getKeyMetricsClicks,
} from '../../../common';
import i18n from './../../../i18n';
import styles from './StrategyAnalysis.module.css';
import { zip } from 'underscore';

// Avoid circular import; define locally
const KEY_METRICS = ['performance', 'income', 'risk', 'sharpe', 'fees'];

const COLORS = {
  blue: '#050139',
  teal: '#05A9B1',
  grey: '#5B6770',
  orange: '#F68D2E',
  auburn: '#84344E',
  sky: '#00A9E0',
  green: '#658D1B',
  white: '#FFFFFF',
  silver: '#BDC2C6',
  navy: '#050139',
};

// ---- tiny helpers (beginner-friendly) ----
function isNonEmptyArray(a) {
  return Array.isArray(a) && a.length > 0;
}
function safeLast(a) {
  return isNonEmptyArray(a) ? a[a.length - 1] : null;
}

// NEW helper: collect buckets from raw totals, adding Cash/Currency and folding Closed Ended into Equity
function getAssetClassBuckets(raw) {
  // Coerce to numbers; raw may be decimals (0–1) or already percentages.
  const n = (v) => Number(v ?? 0);

  // Existing buckets
  let equity = n(raw.Equity);
  const fixed = n(raw['Fixed Income'] ?? raw.FixedIncome);
  const alts = n(raw.Alternatives ?? raw.Alternative);

  // NEW buckets
  const cash = n(raw.Cash ?? raw['Cash & Cash Equivalents']);
  const currency = n(raw.Currency);

  // “Close(d) Ended Fund” should count as Equity
  const closedEndedEquity =
    n(raw['Close Ended Fund']) +
    n(raw['Closed Ended Fund']) +
    n(raw['Closed-End Fund']);

  equity += closedEndedEquity;

  return { equity, fixed, alts, cash, currency };
}
// -----------------------------------------

class StrategyAnalysis extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { cardHovered: null };
  }

  hasPortHubType = (ps, target) => {
    if (!ps) return false;
    const raw = ps.portHubType;
    const match = (v) =>
      String(v || '')
        .trim()
        .toUpperCase() === target;
    return Array.isArray(raw) ? raw.some(match) : match(raw);
  };

  componentDidMount() {
    // Intentionally left blank: avoid mutating the URL when a strategy is opened
    // so that a manual expand does not persist after a browser refresh.
  }

  // Relayout Masonry after hover; only if ref is ready
  setCardHovered = () => {
    setTimeout(() => {
      if (this.masonry && typeof this.masonry.layout === 'function') {
        this.masonry.layout();
      }
    }, 200);
  };

  renderKeyMetrics() {
    const ps = this.props.personalStrategy;
    if (!ps || !ps.statistics) return null;

    const isUserModel = this.hasPortHubType(ps, 'USER-MODEL');
    const metricsValues = getKeyMetrics(ps.statistics);
    const metricsSymbols = getKeyMetricsSymbols();
    const metricClicks = getKeyMetricsClicks();
    const perfAsOf =
      ps.statistics?.stdPerformanceMonthly?.asOfDate || ps.commonPeriodEnd;

    const metricData = KEY_METRICS.map((metric) => {
      const tooltipTemplate = i18n.t(metric + '-tooltip');
      let description = i18n.t(metric + '-desc');
      const commonStart = ps.commonPeriodStart;
      const commonEnd = ps.commonPeriodEnd;

      let tooltip =
        tooltipTemplate &&
        tooltipTemplate
          .replace('[[FROM]]', formatDate(commonStart))
          .replace('[[TO]]', formatDate(commonEnd))
          .replace('[[ASOF]]', formatDate(commonEnd));

      if (metric === 'performance' && !isUserModel) {
        tooltip = `Performance Since Inception As of ${formatDate(perfAsOf)}.`;
        description = 'Avg Annual Returns Since Inception';
      }

      if (metric === 'risk') {
        tooltip = `Risk Since Inception. As of ${formatDate(commonEnd)}.`;
        description = 'Avg Annual Volatility Since Inception';
      }

      if (metric === 'sharpe') {
        tooltip = `Risk Adjusted Returns Since Inception As of ${formatDate(
          commonEnd
        )}.`;
        description = 'Avg Annual Risk Adjusted Returns Since Inception';
      }

      const entry = {
        name: metric,
        title: i18n.t(metric),
        tooltip,
        description,
        value: metricsValues[metric],
        symbol: metricsSymbols[metric],
        clickable: metric === 'income' ? false : metricClicks[metric],
      };

      if (metric === 'income') {
        entry.tooltip = `As of ${formatDate(commonEnd)}.`;
        entry.description = 'Trailing 12M Dividend Yields (%)';
        entry.clickable = metricClicks[metric];
      }

      return entry;
    });

    return (
      <div
        className="pscard"
        key={'keymetrics' + ps.id}
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="key-metrics"
          topOfAppId="personal-strategy-top-of-app"
          key={'keymetrics-card' + ps.id}
          chartType="keymetrics"
          cardTitle={i18n.t('key-metrics-title')}
          seriesData={metricData}
          footnoteText={i18n.t('key-metrics-footnote')}
          showDetailsOnHover={false}
        />
      </div>
    );
  }

  renderFundStdPerformance() {
    const ps = this.props.personalStrategy;
    if (!ps || !isNonEmptyArray(ps.holdings)) return null;

    return (
      <div
        className="pscard pscard--width2"
        key={'fundstdperformance' + ps.id}
        onClick={this.setCardHovered}
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="performance-chart"
          topOfAppId="personal-strategy-top-of-app"
          key={'fundstdperformance-card' + ps.id}
          chartType="performancetable"
          cardTitle={i18n.t('fundstdperformance-title')}
          personalStrategy={ps}
          footnoteText={i18n.t('fundstdperformance-footnote')}
          translationDictionary={i18n}
          showCalYearPerformance={true}
          showDetailsOnHover={false}
          usePipeTabs={true}
        />
      </div>
    );
  }

  renderRiskMetricsCard() {
    const ps = this.props.personalStrategy;
    if (!ps) return null;

    return (
      <div
        className="pscard pscard--width2"
        onClick={this.setCardHovered}
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="risk-metrics-card"
          topOfAppId="personal-strategy-top-of-app"
          key={'riskmetrics-card' + ps.id}
          chartType="riskmetricstable"
          cardTitle="Risk Metrics"
          personalStrategy={ps}
          showDetailsOnHover={false}
        />
      </div>
    );
  }

  renderCharacteristicsCard() {
    const ps = this.props.personalStrategy;
    if (!ps) return null;

    return (
      <div
        className="pscard pscard--width2"
        onClick={this.setCardHovered}
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="characteristics-card"
          topOfAppId="personal-strategy-top-of-app"
          key={'characteristics-card' + ps.id}
          chartType="characteristics"
          cardTitle="Characteristics"
          personalStrategy={ps}
          showDetailsOnHover={false}
          showCharacteristicsDividendBuyback={true}
          showCharacteristicsValuation={true}
          showCharacteristicsSize={true}
          showCharacteristicsQuality={true}
          showCharacteristicsMomentum={false}
          showCharacteristicsGrowth={true}
          showCharacteristicsFixedIncome={false}
          usePipeTabs={true}
        />
      </div>
    );
  }

  renderPerformanceChart() {
    const ps = this.props.personalStrategy;
    if (!ps || !ps.levels || !ps.levels.date) return null;

    const seriesData = [
      {
        data: ps.levels?.uv1MNAV,
        name: ps.name,
        color: COLORS.blue,
      },
    ];

    const bm = isNonEmptyArray(ps.benchmarks)
      ? ps.benchmarks[0]
      : null;

    if (bm && bm.levels && bm.levels.uv1MNAV) {
      seriesData.push({
        data: bm.levels.uv1MNAV,
        name: bm.name,
        color: COLORS.silver,
      });
    }

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="growth-1m"
          topOfAppId="personal-strategy-top-of-app"
          key="performance"
          chartType="line"
          cardTitle={i18n.t('growth')}
          showChartLabels={true}
          seriesCurrencyLabel="$"
          showLegend={false}
          categoryData={ps.levels.date}
          categoryIsDate={true}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          heroTitle={i18n
            .t('hypothetical-growth-title')
            .replace('[[FROM]]', formatDate(ps.commonPeriodStart))
            .replace('[[TO]]', formatDate(ps.commonPeriodEnd))}
          footnoteText={i18n.t('hypothetical-growth-footnote')}
        />
      </div>
    );
  }

  renderIncomeChart() {
    const ps = this.props.personalStrategy;
    const seriesData = ps?.aggregateDistributions?.ordinaryIncome;
    const categoryData = ps?.aggregateDistributions?.calendarYear;

    if (!seriesData || !categoryData) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="income-chart"
          topOfAppId="personal-strategy-top-of-app"
          key="income"
          chartType="column"
          cardTitle={i18n.t('income')}
          showChartLabels={true}
          heroTitle={i18n.t('calendar-year-income-title')}
          showLegend={false}
          categoryData={categoryData}
          categoryIsDate={false}
          seriesIsPercentage={false}
          seriesData={seriesData}
          seriesCurrencyLabel="$"
          showGrid={true}
          showAxes={true}
          footnoteText={i18n.t('calendar-year-income-footnote')}
          showDetailsOnHover={false}
          seriesColor={COLORS.navy}
        />
      </div>
    );
  }

  renderSectorChart() {
    const ps = this.props.personalStrategy;
    const lastPersonalExposure = safeLast(ps?.sectorExposures);
    const bm = isNonEmptyArray(ps?.benchmarks)
      ? ps.benchmarks[0]
      : null;
    const lastBenchmarkExposure = safeLast(bm?.sectorExposures);

    const seriesData = [
      {
        data: lastPersonalExposure?.values ?? null,
        categories: lastPersonalExposure?.keys ?? null,
        name: ps?.name,
        color: COLORS.blue,
      },
    ];

    if (bm && lastBenchmarkExposure) {
      seriesData.push({
        data: lastBenchmarkExposure.values,
        categories: lastBenchmarkExposure.keys,
        name: bm.name,
        color: COLORS.silver,
      });
    }

    if (!isNonEmptyArray(ps?.sectorExposures)) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="sector-exposures"
          topOfAppId="personal-strategy-top-of-app"
          key="sector"
          chartType="bar"
          cardTitle={i18n.t('sectors')}
          showChartLabels={true}
          heroTitle={i18n.t('sector-exposures-title')}
          showLegend={false}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={i18n
            .t('sector-exposures-footnote')
            .replace('[[ASOF]]', formatDate(lastPersonalExposure?.date))}
          showDetailsOnHover={false}
          seriesColor={COLORS.blue}
          seriesIsPercentage={true}
        />
      </div>
    );
  }

  renderGeographyChart() {
    const ps = this.props.personalStrategy;
    const lastCountryExposure = safeLast(ps?.countryExposures);
    const bm = isNonEmptyArray(ps?.benchmarks)
      ? ps.benchmarks[0]
      : null;
    const lastBenchmarkCountryExposure = safeLast(bm?.countryExposures);

    const seriesData = [
      {
        data: lastCountryExposure?.values ?? null,
        categories: lastCountryExposure?.keys ?? null,
        name: ps?.name,
        color: COLORS.blue,
      },
    ];

    if (bm && lastBenchmarkCountryExposure) {
      seriesData.push({
        data: lastBenchmarkCountryExposure.values,
        categories: lastBenchmarkCountryExposure.keys,
        name: bm.name,
        color: COLORS.silver,
      });
    }

    if (!isNonEmptyArray(ps?.countryExposures)) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="geography-exposures"
          topOfAppId="personal-strategy-top-of-app"
          key="geography"
          chartType="bar"
          cardTitle={i18n.t('geographies')}
          showChartLabels={true}
          heroTitle={i18n.t('geography-exposures-title')}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={i18n
            .t('geography-exposures-footnote')
            .replace('[[ASOF]]', formatDate(lastCountryExposure?.date))}
          showDetailsOnHover={false}
          seriesIsPercentage={true}
          sliceCategoriesToN={10}
        />
      </div>
    );
  }

  renderMarketCapChart() {
    const ps = this.props.personalStrategy;
    const lastMarketCap = safeLast(ps?.marketCapExposures);
    const bm = isNonEmptyArray(ps?.benchmarks)
      ? ps.benchmarks[0]
      : null;
    const lastBenchmarkMarketCap = safeLast(bm?.marketCapExposures);

    const mapCap = (key) =>
      key
        .replace('midCap', i18n.t('mid-cap'))
        .replace('largeCap', i18n.t('large-cap'))
        .replace('smallCap', i18n.t('small-cap'));

    const seriesData = [
      {
        data: lastMarketCap?.values ?? null,
        categories: lastMarketCap?.keys ? lastMarketCap.keys.map(mapCap) : null,
        name: ps?.name,
        color: COLORS.blue,
      },
    ];

    if (bm && lastBenchmarkMarketCap) {
      seriesData.push({
        data: lastBenchmarkMarketCap.values,
        categories: lastBenchmarkMarketCap.keys.map(mapCap),
        name: bm.name,
        color: COLORS.silver,
      });
    }

    if (!isNonEmptyArray(ps?.marketCapExposures)) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="market-cap-exposures"
          topOfAppId="personal-strategy-top-of-app"
          key="marketcap"
          chartType="bar"
          cardTitle={i18n.t('market-cap')}
          showChartLabels={true}
          heroTitle={i18n.t('marketcap-exposures-title')}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={i18n
            .t('marketcap-exposures-footnote')
            .replace('[[ASOF]]', formatDate(lastMarketCap?.date))}
          showDetailsOnHover={false}
          seriesColor={COLORS.navy}
          seriesIsPercentage={true}
        />
      </div>
    );
  }

  renderBondMaturity() {
    const ps = this.props.personalStrategy;
    const lastMaturityExposure = safeLast(ps?.maturityExposures);

    const seriesData = [
      {
        data: lastMaturityExposure?.values ?? null,
        categories: lastMaturityExposure?.keys ?? null,
        name: ps?.name,
        color: COLORS.blue,
      },
    ];

    const categories = seriesData[0].categories;
    if (JSON.stringify(categories) === JSON.stringify(['N/A'])) return null;
    if (!isNonEmptyArray(ps?.maturityExposures)) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="bond-maturity-exposures"
          topOfAppId="personal-strategy-top-of-app"
          key="maturity"
          chartType="bar"
          cardTitle={i18n.t('bond-maturity')}
          showChartLabels={true}
          heroTitle={i18n.t('maturity-exposures-title')}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={i18n
            .t('maturity-exposures-footnote')
            .replace('[[ASOF]]', formatDate(lastMaturityExposure?.date))}
          showDetailsOnHover={false}
          seriesIsPercentage={true}
        />
      </div>
    );
  }

  renderCreditQuality() {
    const ps = this.props.personalStrategy;
    const lastCreditExposure = safeLast(ps?.creditQualityExposures);

    const seriesData = [
      {
        data: lastCreditExposure?.values ?? null,
        categories: lastCreditExposure?.keys ?? null,
        name: ps?.name,
        color: COLORS.blue,
      },
    ];

    const categories = seriesData[0].categories;
    if (JSON.stringify(categories) === JSON.stringify(['N/A'])) return null;
    if (!isNonEmptyArray(ps?.creditQualityExposures)) return null;

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="credit-quality-exposures"
          topOfAppId="personal-strategy-top-of-app"
          key="creditquality"
          chartType="bar"
          showChartLabels={true}
          cardTitle={i18n.t('credit-quality')}
          heroTitle={i18n.t('creditquality-exposures-title')}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={i18n
            .t('creditquality-exposures-footnote')
            .replace('[[ASOF]]', formatDate(lastCreditExposure?.date))}
          showDetailsOnHover={false}
          seriesIsPercentage={true}
        />
      </div>
    );
  }

  renderHoldingsChart() {
    const ps = this.props.personalStrategy;
    if (!ps || !isNonEmptyArray(ps.holdings)) return null;

    // latest snapshot
    const holdings = safeLast(ps.holdings);
    if (!holdings || !isNonEmptyArray(holdings.assetClasses)) return null;

    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    const assetClasses = holdings.assetClasses.filter(onlyUnique);
    const zippedData = zip(
      holdings.assetClasses,
      holdings.tickers,
      holdings.weights
    );

    const data = assetClasses.map((assetClass) => {
      const assetClassZip = zippedData.filter((zd) => zd[0] === assetClass);
      const sumWeights = assetClassZip
        .map((x) => x[2])
        .reduce((sum, num) => sum + num, 0);

      return {
        name: assetClass,
        value: formatPercent(sumWeights, 1, false),
        id: assetClass,
        children: assetClassZip.map((holding) => {
          return {
            name: holding[1],
            value: formatPercent(holding[2], 1, false),
            id: assetClass,
            label: { show: true, formatter: '{b}' },
            link: '#fundchar&tickers=' + holding[1],
            target: '_self',
          };
        }),
      };
    });

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="holdings"
          topOfAppId="personal-strategy-top-of-app"
          key="holdings"
          chartType="treemap"
          cardTitle={i18n.t('holdings')}
          showChartLabels={false}
          showLegend={false}
          categoryIsDate={true}
          seriesData={data}
          showGrid={false}
          showAxes={false}
          heroNumber={
            isNonEmptyArray(holdings.tickers) ? holdings.tickers.length : 0
          }
          heroNumberIsPercent={false}
          heroTitle={i18n.t('number-of-holdings')}
          showDetailsOnHover={false}
        />
        <ReactTooltip
          id={'click-to-edit-holdings-tooltip' + ps.id}
          type="light"
          className="ps-tooltip"
          effect="float"
        >
          {i18n.t('click-to-edit-holdings-tooltip')}
        </ReactTooltip>
      </div>
    );
  }

  renderDivider() {
    return (
      <div className="strategy-analysis__divider">
        <div className="strategy-analysis__divider-left" />
        <div className="strategy-analysis__divider-right" />
      </div>
    );
  }

  renderAssetClassBreakdown() {
    const ps = this.props.personalStrategy;
    const raw =
      ps && typeof ps.assetClassTotals === 'object'
        ? ps.assetClassTotals
        : null;
    if (!raw) return null;

    // Pull buckets (now includes Cash & Currency; closed-ended rolled into Equity)
    const { equity, fixed, alts, cash, currency } = getAssetClassBuckets(raw);

    // ASOF should come from the latest holdings snapshot
    const latestHoldings =
      Array.isArray(ps?.holdings) && ps.holdings.length > 0
        ? ps.holdings[ps.holdings.length - 1]
        : null;
    const lastHoldingsDate =
      latestHoldings &&
      (latestHoldings.date || latestHoldings.asOf || latestHoldings.tradeDate);

    const categories = [
      'Equity',
      'Fixed Income',
      'Alternatives',
      'Cash',
      'Currency',
    ];
    const values = [equity, fixed, alts, cash, currency];

    const asOf = lastHoldingsDate || ps?.updateDate || ps?.commonPeriodEnd;

    const seriesData = [{ data: values, categories, name: ps?.name }];

    return (
      <div
        className="pscard"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <AnalysisCard
          idOfCard="asset-class-breakdown"
          topOfAppId="personal-strategy-top-of-app"
          key="asset-class-breakdown"
          chartType="bar"
          cardTitle={i18n.t('assetclass-exposures-title')}
          showChartLabels={true}
          heroTitle={i18n.t('assetclass-exposures-title')}
          categoryIsDate={false}
          seriesData={seriesData}
          showGrid={true}
          showAxes={true}
          footnoteText={
            i18n.t('assetclass-exposures-footnote') &&
            i18n
              .t('assetclass-exposures-footnote')
              .replace('[[ASOF]]', formatDate(asOf))
          }
          showDetailsOnHover={false}
          seriesIsPercentage={true}
        />
      </div>
    );
  }

  renderDisclosuresCard() {
    const lines = [
      i18n.t && i18n.t('excel-disclosure-text-1'),
      i18n.t && i18n.t('excel-disclosure-text-2'),
      i18n.t && i18n.t('excel-disclosure-text-3'),
      i18n.t && i18n.t('excel-disclosure-text-4'),
      i18n.t && i18n.t('excel-disclosure-text-5'),
      i18n.t && i18n.t('excel-disclosure-text-6'),
      i18n.t && i18n.t('excel-disclosure-text-7'),
    ].filter(Boolean);

    if (!lines.length) return null;

    return (
      <div
        className="pscard pscard--width2"
        onMouseEnter={this.setCardHovered}
        onMouseLeave={this.setCardHovered}
      >
        <WTText as="h5" variant="h5" color="navy" className={styles['disclosures-header']}>
          {i18n.t('disclosures') || 'Disclosures'}
        </WTText>
        <div className={styles['disclosures-body']}>
          {lines.map((txt, idx) => (
            <p
              key={'disc-' + idx}
              dangerouslySetInnerHTML={{ __html: txt }}
            />
          ))}
        </div>
      </div>
    );
  }

  render() {
    const keyMetricsChart = this.renderKeyMetrics();
    const performanceChart = this.renderPerformanceChart();
    const incomeChart = this.renderIncomeChart();
    const assetClassChart = this.renderAssetClassBreakdown();
    const holdingsChart = this.renderHoldingsChart();
    const sectorChart = this.renderSectorChart();
    const geographyChart = this.renderGeographyChart();
    const marketCapChart = this.renderMarketCapChart();
    const bondMaturityChart = this.renderBondMaturity();
    const creditQualityChart = this.renderCreditQuality();
    const performanceTable = this.renderFundStdPerformance();
    const riskMetricsCard = this.renderRiskMetricsCard();
    const characteristicsCard = this.renderCharacteristicsCard();
    const disclosuresCard = this.renderDisclosuresCard();

    const masonryOptions = {
      columnWidth: '.pscard--sizer',
      gutter: '.gutter--sizer',
      itemSelector: '.pscard',
      percentPosition: true,
      horizontalOrder: true,
    };

    return (
      <div className={styles['strategy-analysis']}>
        <Masonry
          ref={(c) => {
            if (c && c.masonry) this.masonry = c.masonry;
          }}
          className={'strategy-charts'}
          elementType={'div'}
          options={masonryOptions}
          disableImagesLoaded={false}
          updateOnEachImageLoad={false}
        >
          <div className="pscard card--is-invisible pscard--sizer"></div>
          <div className="pscard gutter--sizer"></div>

          {keyMetricsChart}
          {assetClassChart}
          {holdingsChart}
          {performanceChart}
          {incomeChart}
          {sectorChart}
          {geographyChart}
          {marketCapChart}
          {creditQualityChart}
          {bondMaturityChart}
          {performanceTable}
          {riskMetricsCard}
          {characteristicsCard}
          {disclosuresCard}
        </Masonry>
      </div>
    );
  }
}

StrategyAnalysis.propTypes = {
  openHoldingsEditor: PropTypes.func,
  personalStrategy: PropTypes.object,
};

export default connect(null, {})(StrategyAnalysis);
